import React, { useState } from 'react';
import { withTheme, css } from '@emotion/react'
import { withRouter } from "react-router";
import styled from '@emotion/styled/macro';
import mobileDetector from '../../helpers/mobileDetector';
import SponsorsMenu from './SponsorsMenu';
import PatientsMenu from './PatientsMenu';
import SitesMenu from './SitesMenu';
import Chevron from '../shared/icons/Chevron';

const NAV_HEIGHT = '54px';

const LinkMenuHeader = styled.div`
  align-items: center;  
  color: ${props => props.theme.colors.neutral1};
  display: flex;
  flex-basis: 100%;
  font-family: ${props => props.theme.fonts.primary};
  font-size: ${props => props.theme.fontSizes.sm};
  height: ${NAV_HEIGHT};
  justify-content: space-between;
  padding: 0 ${props => props.theme.margins.std};
  text-decoration: none;
  width: 100%;

  &:visited {
    color: ${props => props.theme.colors.nuetral1}; 
  }

  &.active {
    color: ${props => props.theme.colors.primary2};
  }

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    padding: 0 ${props => props.theme.margins.std} 0 0;
  }
`;

const StyledChevron = styled(Chevron)`
  align-self: center;
  display: flex;
  height: 6px;
  margin: 0;
  position: static;
  right: 14px;
  width: 9px;
  transform: ${props => props.active ? 'rotate(180deg)' : 'rotate(0deg)'};

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    margin: 2px 0 0 8px;
    position: absolute;
    ${props => props.active && css`transform: rotate(0deg);`}
  }
`;

const SubMenu = styled.div`
  display: ${props => props.active ? 'flex' : 'none'};
  font-family: ${props => props.theme.fonts.primary};
  font-size: ${props => props.theme.fontSizes.sm};
  padding: 0 ${props => props.theme.margins.std};
  overflow: hidden;
  position: static;

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    display: ${props => props.active && props.isMobile ? 'flex' : 'none'};
    margin: 0 -10px;
    padding: 0 10px 10px 10px;
    position: absolute;
    top: 54px;
    width: 300px;
  }
`;

const ItemText = styled.span``;

const NavListLinkItem = styled.li`
  align-items: center;
  background-color: ${props => props.theme.colors.contrast2};
  border-bottom: 1px solid ${props => props.theme.colors.neutral3};
  cursor: pointer;
  display: block;
  flex-direction: column;
  height: ${props => props.active ? '100%' : NAV_HEIGHT};
  margin-top: 0;
  position: relative;

  ${SubMenu} {
    ${props => props.active && css`border-top: 3px solid ${props.highlightColor};`}
  }

  ${ItemText} {
    color: ${props => props.active && props.isMobile ? props.highlightColor : props.theme.colors.neutral1};
  }

  &:hover {
    ${StyledChevron} {
      ${props => !props.isMobile && css`transform: rotate(180deg);`};
    }

    ${ItemText} {
      ${props => !props.isMobile && css`color: ${props.highlightColor}`};
    }
  }

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    border-bottom: none;
    display: flex;
    flex-direction: row;
    height: ${NAV_HEIGHT};

    ${ItemText} {
      ${props => props.activeParent && css`color: ${props.highlightColor};`}
    }

    ${SubMenu} {
      ${props => props.active && css`border-top: none;`}
    }

    &:hover {
      ${SubMenu} {
        ${props => !props.isMobile && css`display: flex;`}
      }
    }
  }
`;

function renderSubMenu(menu, active, isMobile) {
  function renderSwitch(menu) {
    switch(menu) {
      case 'sponsors':
        return <SponsorsMenu />;
      case 'patients':
        return <PatientsMenu />;
      case 'sites':
        return <SitesMenu />;
      default:
        return <SponsorsMenu />;
    }
  };

  return (
    <SubMenu isMobile={isMobile} active={active}>
      {renderSwitch(menu)}
    </SubMenu>
  )
}

function NavMenu(props) {
  const [active, setActive] = useState(false);

  const isMobile = mobileDetector();
  const { menu, title, highlightColor, theme, location: { pathname } } = props;
  const regex = new RegExp(menu, 'g');
  const activeParent = !!pathname.match(regex) && !isMobile;
  const chevronColor = active || activeParent ? highlightColor : theme.colors.neutral3;

  function handleMouseOver() {
    if (!isMobile) {
      if (!active) setActive(true);
    }
  }

  function handleMouseOut() {
    if (!isMobile) {
      if (active) setActive(false);
    }
  }

  function handleClick(e) {
    e.preventDefault();
    if (active) {
      setActive(false);
    } else {
      setActive(true);
    }
  }

  return (
    <NavListLinkItem 
      activeParent={activeParent} 
      highlightColor={highlightColor} 
      active={active}
      isMobile={isMobile}
      onClick={e => handleClick(e)}
      onMouseOver={() => handleMouseOver()}
      onMouseOut={() => handleMouseOut()}
    >
      <LinkMenuHeader>
        <ItemText>{title}</ItemText>
        <StyledChevron 
          isMobile={isMobile} 
          activeParent={activeParent} 
          active={active} 
          stroke={chevronColor} 
        />
      </LinkMenuHeader>
      {renderSubMenu(menu, active, isMobile)}
    </NavListLinkItem>
  );
}

export default withRouter(withTheme(NavMenu));